/**
 * @quotech/auth - SSO URL Utilities
 * 
 * Canonical helpers for building SSO URLs and handling cross-hub navigation.
 * Use these instead of manually constructing SSO URLs.
 */

export interface BuildSsoUrlParams {
  /** Target hub base URL (e.g., "https://crm.quotech.ai") */
  hubBaseUrl: string
  /** JWT access token */
  token: string
  /** Organisation ID */
  orgId?: string | null
  /** Organisation name */
  orgName?: string | null
  /** Refresh token (optional) */
  refreshToken?: string | null
  /** Path to redirect to after SSO (e.g., "/quotes/123") */
  returnTo?: string | null
  /** Additional context to pass (will be base64 encoded) */
  context?: Record<string, unknown> | null
}

/**
 * Build a canonical SSO URL for cross-hub navigation
 * 
 * @example
 * ```ts
 * const url = buildSsoUrl({
 *   hubBaseUrl: 'https://crm.quotech.ai',
 *   token: accessToken,
 *   orgId: '123',
 *   orgName: 'Acme Corp',
 *   returnTo: '/quotes/456',
 * })
 * // => https://crm.quotech.ai/sso?token=...&org_id=123&org_name=Acme%20Corp&returnTo=/quotes/456
 * ```
 */
export function buildSsoUrl({
  hubBaseUrl,
  token,
  orgId,
  orgName,
  refreshToken,
  returnTo,
  context,
}: BuildSsoUrlParams): string {
  // Normalize base URL (remove trailing slash)
  const baseUrl = hubBaseUrl.replace(/\/$/, '')
  
  const params = new URLSearchParams()
  
  // Required: token
  params.set('token', token)
  
  // Optional params
  if (orgId) params.set('org_id', orgId)
  if (orgName) params.set('org_name', orgName)
  if (refreshToken) params.set('refresh_token', refreshToken)
  if (returnTo) params.set('returnTo', returnTo)
  if (context) params.set('context', encodeContext(context))
  
  return `${baseUrl}/sso?${params.toString()}`
}

/**
 * Build a cross-hub link with SSO (convenience wrapper)
 * 
 * This is the preferred way to create links to other hubs.
 * It automatically handles SSO token passing.
 * 
 * @example
 * ```ts
 * const link = buildHubLink({
 *   hub: 'crm',
 *   path: '/quotes/456',
 *   token: accessToken,
 *   orgId: currentOrgId,
 * })
 * ```
 */
export function buildHubLink(params: {
  hub: 'jobs' | 'crm' | 'qa' | 'qdrive' | 'workforce' | 'cfo' | 'quote' | 'billing' | 'email' | 'hr' | 'inventory' | 'assets' | 'ai' | 'smartdocs'
  path?: string
  token: string
  orgId?: string | null
  orgName?: string | null
  context?: Record<string, unknown> | null
}): string {
  // Environment-aware hub URLs
  const DEV_HUB_PORTS: Record<string, number> = {
    jobs: 5174, crm: 5175, qa: 3004, qdrive: 5176, workforce: 5177,
    cfo: 5173, quote: 5183, billing: 5181, email: 5182, hr: 5185,
    inventory: 5186, assets: 5187, ai: 5184, smartdocs: 5189,
  }
  const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV
  const getHubUrl = (hubId: string): string => {
    if (isDev && DEV_HUB_PORTS[hubId]) {
      return `http://localhost:${DEV_HUB_PORTS[hubId]}`
    }
    if (hubId === 'jobs') return 'https://dashboard.quotech.ai'
    // SmartDocs is served in-shell under the dashboard (no standalone subdomain),
    // mirroring developer. smartdocs.quotech.ai is intentionally unused (NXDOMAIN).
    if (hubId === 'smartdocs') return 'https://dashboard.quotech.ai/smartdocs'
    return `https://${hubId}.quotech.ai`
  }
  
  const hubUrls: Record<string, string> = {
    jobs: getHubUrl('jobs'),
    crm: getHubUrl('crm'),
    qa: getHubUrl('qa'),
    qdrive: getHubUrl('qdrive'),
    workforce: getHubUrl('workforce'),
    cfo: getHubUrl('cfo'),
    quote: getHubUrl('quote'),
    billing: getHubUrl('billing'),
    email: getHubUrl('email'),
    hr: getHubUrl('hr'),
    inventory: getHubUrl('inventory'),
    assets: getHubUrl('assets'),
    ai: getHubUrl('ai'),
    smartdocs: getHubUrl('smartdocs'),
  }
  
  const hubBaseUrl = hubUrls[params.hub]
  if (!hubBaseUrl) {
    console.warn(`[@quotech/auth] Unknown hub: ${params.hub}`)
    return '#'
  }
  
  return buildSsoUrl({
    hubBaseUrl,
    token: params.token,
    orgId: params.orgId,
    orgName: params.orgName,
    returnTo: params.path,
    context: params.context,
  })
}

// =============================================================================
// Context encoding/decoding (for job-centric navigation)
// =============================================================================

export interface JobContext {
  jobId: string
  jobNumber: string
  jobName: string
  healthScore?: number
}

/**
 * Encode context object to base64 for URL parameter
 */
export function encodeContext(context: Record<string, unknown>): string {
  try {
    const json = JSON.stringify(context)
    // Use btoa with encodeURIComponent for Unicode safety
    return btoa(encodeURIComponent(json))
  } catch (err) {
    console.error('[@quotech/auth] Failed to encode context:', err)
    return ''
  }
}

/**
 * Decode context from base64 URL parameter
 */
export function decodeContext<T = Record<string, unknown>>(encoded: string): T | null {
  try {
    // Use atob with decodeURIComponent for Unicode safety
    const json = decodeURIComponent(atob(encoded))
    return JSON.parse(json) as T
  } catch (err) {
    console.error('[@quotech/auth] Failed to decode context:', err)
    return null
  }
}

/**
 * Extract job context from URL search params
 */
export function extractJobContext(searchParams: URLSearchParams): JobContext | null {
  const contextStr = searchParams.get('context') || searchParams.get('jobContext')
  if (!contextStr) return null
  
  const context = decodeContext<JobContext>(contextStr)
  if (!context || !context.jobId) return null
  
  return context
}

/**
 * Build a job context object for cross-hub navigation
 */
export function buildJobContext(job: {
  id: string
  jobNumber?: string
  job_no?: string
  jobName?: string
  name?: string
  healthScore?: number
  health_score?: number
}): JobContext {
  return {
    jobId: job.id,
    jobNumber: job.jobNumber || job.job_no || '',
    jobName: job.jobName || job.name || '',
    healthScore: job.healthScore ?? job.health_score,
  }
}

// =============================================================================
// Hub detection utilities
// =============================================================================

/**
 * Get the current hub based on window.location.hostname
 */
export function getCurrentHub(): string {
  if (typeof window === 'undefined') return 'jobs'
  
  const hostname = window.location.hostname
  
  if (hostname.includes('crm.')) return 'crm'
  if (hostname.includes('qa.')) return 'qa'
  if (hostname.includes('qdrive.')) return 'qdrive'
  if (hostname.includes('workforce.')) return 'workforce'
  if (hostname.includes('cfo.')) return 'cfo'
  if (hostname.includes('quote.')) return 'quote'
  if (hostname.includes('billing.')) return 'billing'
  if (hostname.includes('email.')) return 'email'
  if (hostname.includes('hr.')) return 'hr'
  if (hostname.includes('inventory.')) return 'inventory'
  if (hostname.includes('assets.')) return 'assets'
  if (hostname.includes('ai.')) return 'ai'
  
  // Default to jobs (dashboard)
  return 'jobs'
}

/**
 * Check if we're in the main dashboard (Jobs hub)
 */
export function isMainDashboard(): boolean {
  return getCurrentHub() === 'jobs'
}

